import { PillSearchBar } from "@/components/common/PillSearchBar";
import { FloatingControlsBar } from "@/components/projects/FloatingControlsBar";
import type { Category } from "@/contexts/CategoriesContext";
import {
  FoldVertical,
  MoveHorizontal,
  PanelBottomClose,
  PanelBottomOpen,
  UnfoldVertical,
} from "lucide-react";

interface ProjectsControlsProps {
  searchPills: string[];
  onSearchPillsChange: (pills: string[]) => void;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  showAvailabilityDock: boolean;
  onToggleAvailabilityDock: () => void;
  showTimelineDateSelector: boolean;
  onToggleTimelineDateSelector: () => void;
  /** Categories that render hidden by default (collapsed_by_default), each with a show/hide toggle. */
  collapsibleCategories: Category[];
  /** Which collapsed-by-default categories are currently revealed (category id -> shown). */
  revealedCategories: Record<string, boolean>;
  onToggleCategory: (categoryId: string) => void;
}

function ControlsInner({
  searchPills,
  onSearchPillsChange,
  searchInput,
  onSearchInputChange,
  onExpandAll,
  onCollapseAll,
  showAvailabilityDock,
  onToggleAvailabilityDock,
  showTimelineDateSelector,
  onToggleTimelineDateSelector,
  collapsibleCategories,
  revealedCategories,
  onToggleCategory,
}: ProjectsControlsProps) {
  return (
    <>
      {collapsibleCategories.map((category) => {
        const revealed = !!revealedCategories[category.id];
        return (
          <div className="board-controls__sold-active-toggle" key={category.id}>
            <label className="board-controls__toggle-label">
              <input
                type="checkbox"
                checked={revealed}
                onChange={() => onToggleCategory(category.id)}
              />
              <span
                className="board-controls__toggle-switch"
                aria-hidden="true"
              />
              <span>
                {revealed ? "Hide" : "Show"} {category.name}
              </span>
            </label>
          </div>
        );
      })}
      <div className="board-controls__search-row">
        <PillSearchBar
          pills={searchPills}
          onPillsChange={onSearchPillsChange}
          inputValue={searchInput}
          onInputChange={onSearchInputChange}
          placeholder="Search projects..."
        />
        <div className="board-controls__expand-collapse">
          <button
            type="button"
            className={`board-controls__dock-toggle ${showTimelineDateSelector ? "board-controls__dock-toggle--active" : ""}`}
            onClick={onToggleTimelineDateSelector}
            title={
              showTimelineDateSelector
                ? "Hide Scroll to Date"
                : "Show Scroll to Date"
            }
            aria-label={
              showTimelineDateSelector
                ? "Hide Scroll to Date"
                : "Show Scroll to Date"
            }
            aria-pressed={showTimelineDateSelector}
          >
            <MoveHorizontal size={18} aria-hidden="true" />
            <span>Date</span>
          </button>
          <button
            type="button"
            className={`board-controls__dock-toggle ${showAvailabilityDock ? "board-controls__dock-toggle--active" : ""}`}
            onClick={onToggleAvailabilityDock}
            title={
              showAvailabilityDock ? "Hide Availability" : "Show Availability"
            }
            aria-label={
              showAvailabilityDock ? "Hide Availability" : "Show Availability"
            }
            aria-pressed={showAvailabilityDock}
          >
            {showAvailabilityDock ? (
              <PanelBottomClose size={18} aria-hidden="true" />
            ) : (
              <PanelBottomOpen size={18} aria-hidden="true" />
            )}
            <span>Availability</span>
          </button>
          <span className="board-controls__expand-collapse-label">
            Expand/Collapse All Projects
          </span>
          <button
            type="button"
            className="board-controls__icon-button"
            onClick={onExpandAll}
            title="Expand All Projects"
            aria-label="Expand All Projects"
          >
            <UnfoldVertical size={18} />
          </button>
          <button
            type="button"
            className="board-controls__icon-button"
            onClick={onCollapseAll}
            title="Collapse All Projects"
            aria-label="Collapse All Projects"
          >
            <FoldVertical size={18} />
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * Unified controls for Projects page
 * Includes office filter, search bar, and expand/collapse.
 * Floats as a fixed bar at the top of the viewport when scrolled off screen.
 */
export function ProjectsControls(props: ProjectsControlsProps) {
  return (
    <FloatingControlsBar>
      <ControlsInner {...props} />
    </FloatingControlsBar>
  );
}
